import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LandingpageComponent } from './views/auth/landingpage/landingpage.component';
import {
  redirectLoggedInTo,
  redirectUnauthorizedTo,
  canActivate,
} from '@angular/fire/auth-guard';
import { NormaluserguardGuard } from './core/authguards/normaluser.guard';
import { CustomeruserguardGuard } from './core/authguards/customeruser.guard';

import { ServicemanagerLayoutComponent } from './core/layouts/servicemanager-layout/servicemanager-layout.component';
import { NormaluserLayoutComponent } from './core/layouts/normaluser-layout/normaluser-layout.component';
import { ServicemanagerguardGuard } from './core/authguards/servicemanager.guard';
import { BusinessuserguardGuard } from './core/authguards/businessuser.guard';
import { BusinessuserLayoutComponent } from './core/layouts/businessuser-layout/businessuser-layout.component';
import { PlainLayoutComponent } from './core/layouts/plain-layout.component';
import { TimesheetEntryDialogComponent } from './views/auth/booking-orders/timesheet-entry-dialog/timesheet-entry-dialog.component';
import { BookingDetailComponent } from './views/auth/booking-detail/booking-detail.component';
import { AdminuserLayoutComponent } from './core/layouts/adminuser-layout/adminuser-layout.component';
import { AdminguardGuard } from './core/authguards/admin.guard';
import { CategoryDetailComponent } from './views/auth/category-detail/category-detail.component';
import { BookingOrdersComponent } from './views/auth/booking-orders/booking-orders.component';
import { ConfirmEnquiryComponent } from './views/auth/booking-detail/confirm-enquiry/confirm-enquiry.component';
import { BookingGarageComponent } from './views/auth/booking-garage/booking-garage.component';
import { OtherServicesComponent } from './views/auth/other-services/other-services.component';
import { RegisterBusinessComponent } from './views/auth/register-business/register-business.component';
import { VehiclePlanDialogComponent } from './components/vehicle-plan-dialog/vehicle-plan-dialog.component';
import { CartComponent } from './views/auth/cart/cart.component';
import { ProfileComponent } from './views/auth/profile/profile.component';
import { RegisterComponent } from './views/auth/register/register.component';
import { LoginComponent } from './views/auth/login/login.component';
import { OrderDetailsComponent } from './views/auth/booking-orders/order-details/order-details.component';
import { RoleSetupPageComponent } from './views/auth/role-setup/role-setup.component';
import { BusinessDetailGuard } from './core/authguards/business-detail.guard';
import { UserNotificationsComponent } from './views/auth/notifications/notifications.component';
import { PrivacyPolicyComponent } from './views/auth/privacy-policy/privacy-policy.component';
import { TermsConditionsComponent } from './views/auth/terms-conditions/terms-conditions.component';
import { CompanyDetailsComponent } from './views/auth/company-details/company-details.component';
import { HelpCenterComponent } from './views/auth/help-center/help-center.component';
import { RentalResponsibilityComponent } from './views/auth/rental-responsibility/rental-responsibility.component';

const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['']);
const redirectLoggedIntoHome = () => redirectLoggedInTo(['/home']);
export const routes: Routes = [
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },

  // 🔹 Public Auth Module
  {
    path: 'auth',
    component: NormaluserLayoutComponent,
    loadChildren: () =>
      import('./views/auth/auth.module').then((m) => m.AuthModule),
    ...canActivate(redirectLoggedIntoHome),
  },

  // 🔹 Normal User Layout (with Navbar + Footer)
  {
    path: '',
    component: NormaluserLayoutComponent, // << Navbar + Footer live here
    children: [
      { path: 'home', component: LandingpageComponent },
      { path: 'booking-detail/:id', component: BookingDetailComponent },
      { path: 'notifications', component: UserNotificationsComponent },
      { 
        path: 'confirm-enquiry/:id', 
        component: ConfirmEnquiryComponent
      },
      { path: 'booking-orders', component: BookingOrdersComponent },
      { path: 'booking-orders/:id', component: OrderDetailsComponent },
      { path: 'booking-garage', component: BookingGarageComponent },
      { path: 'register-business', component: RegisterBusinessComponent },
      { path: 'register', component: RegisterComponent },
      { path: 'login', component: LoginComponent },
      { path: 'setup-role', component: RoleSetupPageComponent },
      { path: 'select-plan', component: VehiclePlanDialogComponent },
      { path: 'cart', component: CartComponent },
      { path: 'profile', component: ProfileComponent },

      { path: 'other-services', component: OtherServicesComponent },

      {
        path: 'category',
        component: CategoryDetailComponent,
      },
      { path: 'category/:id', component: CategoryDetailComponent },
      { path: 'privacy', component: PrivacyPolicyComponent },
      { path: 'privacy-policy', redirectTo: 'privacy', pathMatch: 'full' },
      { path: 'terms', component: TermsConditionsComponent },
      { path: 'terms-and-conditions', redirectTo: 'terms', pathMatch: 'full' },
      { path: 'company-details', component: CompanyDetailsComponent },
      { path: 'help-center', component: HelpCenterComponent },
      { path: 'rental-responsibility', component: RentalResponsibilityComponent },
    ],
  },

  // 🔹 Service Manager Layout
  {
    path: 'servicemanager',
    component: ServicemanagerLayoutComponent,
    ...canActivate(redirectUnauthorizedToLogin),
    canActivateChild: [ServicemanagerguardGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./views/servicemanagerview/servicemanagerview.module').then(
            (m) => m.ServicemanagerviewModule,
          ),
      },
    ],
  },

  // 🔹 Customer Layout
  {
    path: 'customer',
    component: NormaluserLayoutComponent,
    ...canActivate(redirectUnauthorizedToLogin),
    canActivateChild: [CustomeruserguardGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./views/customerview/customerview.module').then(
            (m) => m.CustomerViewModule,
          ),
      },
    ],
  },

  // 🔹 Business Layout
  {
    path: 'business',
    component: BusinessuserLayoutComponent,
    ...canActivate(redirectUnauthorizedToLogin),
    canActivateChild: [BusinessuserguardGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./views/businessview/businessview.module').then(
            (m) => m.BusinessViewModule,
          ),
      },
    ],
  },

  // 🔹 Business Layout
  {
    path: 'admin',
    component: AdminuserLayoutComponent,
    ...canActivate(redirectUnauthorizedToLogin),
    canActivateChild: [AdminguardGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./views/adminview/adminview.module').then(
            (m) => m.AdminViewModule,
          ),
      },
    ],
  },

  // 🔹 Plain Layout (Logo only) - For Timesheet
  {
    path: 'timesheet/:id',
    component: PlainLayoutComponent,
    children: [{ path: '', component: TimesheetEntryDialogComponent }],
  },

  // dummy vendor
  {
    path: 'dummy',
    loadComponent: () =>
      import('./views/dummy-vendor/dummy-vendor.component').then(
        (m) => m.DummyVendorComponent,
      ),
  },
];
@NgModule({
  imports: [
    RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
